import React, { useRef, useState, useEffect } from "react";
import Button from "../../components/button";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelectedFiles } from "../../hooks/use-selected-files";
import { useSections } from "../../hooks/use-sections";
import { useTour } from "../../contexts/tour-context";
import attachIcon from "../../assets/attach.svg";
import { ReactComponent as InfoIcon } from "../../assets/info.svg";
import DocumentIcon from "../../icons/DocumentIcon";
import CrossIcon from "../../icons/CrossIcon";
import UploadCloudIcon from "../../icons/UploadCloudIcon";
import PageContainer from "../../components/page-container";
import s from "./styles.module.scss";
import PageHeader from "../../components/page-header";
import Modal from "../../components/modal"; 


const MAX_FILES = 9;
const ALLOWED_FILE_TYPES = [".pdf", ".docx"];
const ALLOWED_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const InsertSyllabi = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateSections } = useSections();
  const { handleFileSelect, handleUpload, selectedFiles } = useSelectedFiles();
  const { resetTour, startTour } = useTour();
  const [dragOver, setDragOver] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "error",
    title: "",
    message: "",
    primaryButtonText: "OK",
    secondaryButtonText: null,
    onPrimaryClick: null,
    onSecondaryClick: null,
  });

  // Fire Meta Pixel CompleteRegistration if user just signed up (OAuth or email)
  useEffect(() => {
    const pendingPixel = sessionStorage.getItem("pending_signup_pixel");
    if (pendingPixel) {
      sessionStorage.removeItem("pending_signup_pixel");
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'CompleteRegistration');
      }
    }
  }, []);

  useEffect(() => {
    if (location.state?.error) {
      const errorResponse = location.state.error;
      
      if (errorResponse?.error_type === "file_size_exceeded") {
        // Set modal state here
        setModalState({
          isOpen: true,
          title: 'File Size Limit Exceeded',
          message: "A file you inserted was over the limit of 50 pages",
          type: 'error',
          primaryButtonText: "OK",
        });
        
        // Clear the error from location state
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state]);

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const handleGenerateSections = () => {
    if (selectedFiles.length > 0) {
      navigate("/loading", {
        state: {
          message: "Loading sections",
          description: "This may take a moment while we generate your sections",
        },
      });
      handleUpload({
        onSuccess: (response) => {
          const sections = response.courses || response;

          updateSections(sections);

          let needsTimezone = false;
          let fallbackRequired = true;
          try {
            const timezoneValue = localStorage.getItem('needs_timezone_selection');
            const fallbackValue = localStorage.getItem("fallback_required");
            if (timezoneValue !== null) {
              needsTimezone = JSON.parse(timezoneValue);
              console.log("Parsed needs_timezone_selection:", needsTimezone);
            }
            if (fallbackValue !== null) {
              fallbackRequired = JSON.parse(fallbackValue);
            }
          } catch (error) {
            console.warn(
              "Failed to parse fallback_required from localStorage:",
              error
            );
            fallbackRequired = true;
          }
          console.log("Needs timezone selection:", needsTimezone);
          if (needsTimezone) {
            navigate("/timezone-selection", { replace: true });
        } else if (fallbackRequired) {
            navigate("/choose-sections", { replace: true });
        } else {
            navigate("/select-sections", { replace: true });
        }
        },
        onError: (errorResponse) => {
          console.log("Error response received:", errorResponse);
          
          // Navigate back from loading to upload page
          navigate("/upload", { replace: true,
            state: {
              error: errorResponse
            }
           });
        },
      });
    }
  };


  const handleModalClose = () => {
    setModalState({ ...modalState, isOpen: false });
    
    if (modalState.hasValidFiles) {
      // Navigate to the appropriate screen for valid files
      let needsTimezone = false;
      let fallbackRequired = true;
      try {
        const timezoneValue = localStorage.getItem('needs_timezone_selection');
        const fallbackValue = localStorage.getItem("fallback_required");
        if (timezoneValue !== null) {
          needsTimezone = JSON.parse(timezoneValue);
        }
        if (fallbackValue !== null) {
          fallbackRequired = JSON.parse(fallbackValue);
        }
      } catch (error) {
        console.warn("Failed to parse from localStorage:", error);
        fallbackRequired = true;
      }
      
      if (needsTimezone) {
        navigate("/timezone-selection", { replace: true });
      } else if (fallbackRequired) {
        navigate("/choose-sections", { replace: true });
      } else {
        navigate("/select-sections", { replace: true });
      }
    }
    // If no valid files, user stays on upload page to try again
  };

  const fileInputRef = useRef(null);

  const handleSelectFilesClick = () => {
    fileInputRef.current.click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const newFiles = Array.from(e.dataTransfer.files);
    addNewFiles(newFiles);
  };

  const isValidFileType = (file) => {
    const fileExtension = "." + file.name.split(".").pop().toLowerCase();
    const hasValidExtension = ALLOWED_FILE_TYPES.includes(fileExtension);

    const hasValidMimeType = ALLOWED_MIME_TYPES.includes(file.type);

    return hasValidExtension && hasValidMimeType;
  };

  const addNewFiles = (newFiles) => {
    const validFiles = newFiles.filter((file) => {
      if (!isValidFileType(file)) {
        alert(
          `"${file.name}" is not a supported file type. Please upload .pdf or .docx files only.`
        );
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) {
      return;
    }

    // Filter out duplicates based on file name and size
    const uniqueNewFiles = validFiles.filter(
      (newFile) =>
        !selectedFiles.some(
          (existingFile) =>
            existingFile.name === newFile.name &&
            existingFile.size === newFile.size
        )
    );

    if (uniqueNewFiles.length > 0) {
      const availableSlots = MAX_FILES - selectedFiles.length;

      if (availableSlots <= 0) {
        alert(
          `Maximum of ${MAX_FILES} files allowed. Please remove some files before adding new ones.`
        );
        return;
      }

      const filesToAdd = uniqueNewFiles.slice(0, availableSlots);
      const updatedFiles = [...selectedFiles, ...filesToAdd];

      if (uniqueNewFiles.length > availableSlots) {
        alert(
          `Only ${availableSlots} file(s) can be added. Maximum of ${MAX_FILES} files allowed.`
        );
      }

      handleFileSelect(updatedFiles);
    }
  };

  const handleFileInputChange = (e) => {
    const newFiles = Array.from(e.target.files);
    addNewFiles(newFiles);
    // Clear the input so the same file can be selected again if removed and re-added
    e.target.value = "";
  };

  const removeFile = (indexToRemove) => {
    const updatedFiles = selectedFiles.filter(
      (_, index) => index !== indexToRemove
    );
    handleFileSelect(updatedFiles);
  };

  const handleRestartTour = async () => {
    await resetTour();
    startTour();
  };

  return (
    <>
      <PageContainer>
        <button
          className={s.tourInfoButton}
          onClick={handleRestartTour}
          title="Restart tour"
          aria-label="Restart tour"
        >
          <InfoIcon className={s.infoIcon} />
        </button>
        <PageHeader
          title="Insert syllabi here"
          description="Upload up to 9 pdf or docx files containing course syllabi"
        />

        <div
          className={`${s.dropZone} ${dragOver ? s.dropZone + "--dragOver" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          data-tour="upload-dropzone"
        >
          <UploadCloudIcon
            color={selectedFiles.length > 0 ? "#5B82F3" : "#6B7280"}
            size={48}
          />
          <p className={s.dropZoneText}>
            Drag and drop files or click below to attach files
          </p>

          <Button
            variant="secondary"
            size="medium"
            onClick={handleSelectFilesClick}
            icon={
              <img src={attachIcon} alt="Attach" className={s.buttonIconImg} />
            }
            className={s.attachButton}
          >
            Attach
          </Button>
        </div>

        {selectedFiles.length > 0 && (
          <div className={s.selectedFiles}>
            {selectedFiles.map((file, index) => (
              <div key={index} className={s.fileTag}>
                <DocumentIcon color="#5B82F3" size={24} />
                <span className={s.fileName}>{file.name}</span>
                <button
                  className={s.removeFileBtn}
                  onClick={() => removeFile(index)}
                  type="button"
                >
                  <CrossIcon color="#6B7280" size={24} />
                </button>
              </div>
            ))}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInputChange}
          className={s.hiddenFileInput}
          accept=".pdf,.doc,.docx"
        />

        <Button
          variant="primary"
          size="medium"
          disabled={selectedFiles.length === 0}
          onClick={handleGenerateSections}
        >
          Generate sections
        </Button>
      </PageContainer>

      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        primaryButtonText={modalState.primaryButtonText}
        secondaryButtonText={modalState.secondaryButtonText}
        onPrimaryClick={modalState.onPrimaryClick}
        onSecondaryClick={modalState.onSecondaryClick}
      />
    </>
  );
};

export default InsertSyllabi;
